import * as pdfjsLib from "pdfjs-dist";
import { ExtractedFields, ValidationIssue, FieldName } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export const siteMapping: { [key: string]: string } = {
  "greenwich": "NHS Circle - Greenwich",
  "rushcliffe": "NHS Circle - Rushcliffe",
  "north hampshire": "NHS Circle - North Hampshire",
  "bedford": "NHS Circle - Bedforshire",
  "bedfordshire": "NHS Circle - Bedforshire",
  "south & west herts": "NHS Circle - South and West Hertfordshire",
  "south west herts": "NHS Circle - South and West Hertfordshire",
  "south and west hertfordshire": "NHS Circle - South and West Hertfordshire",
  "hertfordshire": "NHS Circle - South and West Hertfordshire",
  "herts": "NHS Circle - South and West Hertfordshire",
  "highlands": "NHS Highlands",
  "leicestershire": "NHS Leicestershire",
  "nottingham": "NHS Nottingham",
};

export function normalizeSite(input: string): string {
  if (!input) return "";
  const lower = input.trim().toLowerCase();
  return siteMapping[lower] || input;
}

export interface ExtractionResult {
  fields: ExtractedFields;
  rawText: string;
  validationIssues: ValidationIssue[];
}

export async function extractPdfWithValidation(file: File): Promise<ExtractionResult> {
  let rawText = "";
  const validationIssues: ValidationIssue[] = [];

  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item: any) => item.str).join(" ");
      rawText += pageText + "\n";
    }
  } catch {
    return {
      fields: {},
      rawText: "",
      validationIssues: [{ field: "file", type: "error", message: "Could not read PDF file" }],
    };
  }

  const cleanedText = cleanupUkReferralText(rawText);
  const fields = parseNhsReferral(cleanedText);

  const mismatches = validateAgainstSource(fields, rawText);
  for (const m of mismatches) {
    validationIssues.push({
      field: m.field,
      type: "mismatch",
      message: `${m.label} not found in source PDF`,
    });
  }

  const missingFields = validateRequiredFields(fields);
  for (const m of missingFields) {
    validationIssues.push({
      field: m.field,
      type: "missing",
      message: `${m.label} is required`,
    });
  }

  return { fields, rawText, validationIssues };
}

interface FieldIssue {
  field: FieldName | "contact";
  label: string;
}

function validateAgainstSource(fields: ExtractedFields, rawText: string): FieldIssue[] {
  const mismatches: FieldIssue[] = [];
  const normalizedText = rawText.toLowerCase().replace(/\s+/g, " ");

  if (fields.nhs_number) {
    const nhsDigits = fields.nhs_number.replace(/\s/g, "");
    if (!rawText.replace(/\s/g, "").includes(nhsDigits)) {
      mismatches.push({ field: "nhs_number", label: "NHS Number" });
    }
  }

  if (fields.email) {
    const emailPart = fields.email.split("@")[0].toLowerCase();
    if (!normalizedText.includes(emailPart)) {
      mismatches.push({ field: "email", label: "Email" });
    }
  }

  if (fields.pathway_ids) {
    const ids = fields.pathway_ids.split(",").map(id => id.trim());
    for (const id of ids) {
      if (id && !rawText.toLowerCase().includes(id.toLowerCase())) {
        mismatches.push({ field: "pathway_ids", label: "Pathway ID" });
        break;
      }
    }
  }

  return mismatches;
}

function validateRequiredFields(fields: ExtractedFields): FieldIssue[] {
  const missing: FieldIssue[] = [];

  if (!fields.patient_name) missing.push({ field: "patient_name", label: "Patient Name" });
  if (!fields.nhs_number) missing.push({ field: "nhs_number", label: "NHS Number" });
  if (!fields.site) missing.push({ field: "site", label: "Site" });
  if (!fields.pathway_ids) missing.push({ field: "pathway_ids", label: "Pathway ID" });
  if (!fields.body_parts || fields.body_parts.length === 0) missing.push({ field: "body_parts", label: "Body Parts" });
  if (!fields.invited_joint) missing.push({ field: "invited_joint", label: "Invited Joint" });
  if (!fields.email && !fields.phone) missing.push({ field: "contact", label: "Email or Phone" });

  return missing;
}

function cleanupUkReferralText(text: string): string {
  const startMarker = "Referral Letter";
  const endMarker = "No AIS information to display";

  const startIdx = text.indexOf(startMarker, 100);
  if (startIdx === -1) return text;

  const endIdx = text.indexOf(endMarker, startIdx);
  if (endIdx === -1) return text.slice(startIdx).trim();

  return text.slice(startIdx, endIdx).trim();
}

function parseNhsReferral(text: string): ExtractedFields {
  const fields: ExtractedFields = {};

  let siteMatch = text.match(/Additional Services,?\s*([A-Za-z\s&]+?)(?=\s*(?:Re:|Referral|Dear|\d{1,2}\s+[A-Za-z]+\s+\d{4}))/i);
  if (!siteMatch) {
    siteMatch = text.match(/Additional Services,?[\s\n]*([A-Za-z]+)/i);
  }
  if (!siteMatch) {
    const headerArea = text.slice(0, 500).toLowerCase();
    for (const siteName of Object.keys(siteMapping)) {
      if (headerArea.includes(siteName)) {
        fields.site = siteMapping[siteName];
        break;
      }
    }
  }
  if (siteMatch && !fields.site) {
    const pdfSite = siteMatch[1].trim();
    fields.site = siteMapping[pdfSite.toLowerCase()] || pdfSite;
  }

  const dateMatch = text.match(/Referral Letter[\s\S]{0,50}?(\d{1,2}\s+[A-Za-z]+\s+\d{4})/);
  if (dateMatch) {
    const parsed = parseDate(dateMatch[1]);
    if (parsed) fields.referral_date = parsed;
  }
  if (!fields.referral_date) {
    const standaloneDateMatch = text.match(/(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4})/i);
    if (standaloneDateMatch) {
      const parsed = parseDate(standaloneDateMatch[1]);
      if (parsed) fields.referral_date = parsed;
    }
  }

  const urgencyMatch = text.match(/URGENCY\s*:?\s*(Routine|Urgent|2-week wait)/i);
  if (urgencyMatch) {
    const urgency = urgencyMatch[1].trim().toLowerCase();
    if (urgency === "routine") fields.urgency = "Routine";
    else if (urgency === "urgent") fields.urgency = "Urgent";
    else if (urgency === "2-week wait") fields.urgency = "2-week wait";
  }

  const pathwayRaw = text.match(/PathwayID:?\s*([\s\S]*?)(?=\s*Body Part)/i)?.[1] || "";

  let bodyPartRaw = text.match(/Body Part Referred:\s*([\s\S]*?)(?=\s*Reason\s+for\s+Referral)/i)?.[1] || "";
  if (!bodyPartRaw) {
    bodyPartRaw = text.match(/Body Part Referred:\s*([\s\S]*?)(?=\s*Reason\s*:)/i)?.[1] || "";
  }
  if (!bodyPartRaw) {
    bodyPartRaw = text.match(/Body Part Referred:\s*([\s\S]{1,300}?)(?=\s*(?:Reason|Dear\s+Joint|Re:|GP:|Telephone))/i)?.[1] || "";
  }

  const bodyPartKeywords = /knee|hip|shoulder|elbow|spine|lumbar|back|neck|hand|wrist|foot|ankle|leg/i;
  const hexIdPattern = /[a-f0-9]{10,}/i;

  const pathwayHasBodyPart = bodyPartKeywords.test(pathwayRaw);
  const bodyPartHasHexId = hexIdPattern.test(bodyPartRaw);
  const actualPathwayText = (pathwayHasBodyPart && bodyPartHasHexId) ? bodyPartRaw : pathwayRaw;
  const actualBodyPartText = (pathwayHasBodyPart && bodyPartHasHexId) ? pathwayRaw : bodyPartRaw;

  const pathwayIds = actualPathwayText
    .split(/[\s,]+/)
    .map(id => id.trim())
    .filter(id => id && /^[a-f0-9]{10,}$/i.test(id));
  if (pathwayIds.length > 0) {
    fields.pathway_ids = pathwayIds.join(", ");
  }

  let rawParts = actualBodyPartText
    .replace(/(Left|Right|Bilateral)\s+(?=Left|Right|Bilateral)/gi, "$1\n")
    .split(/[\n,]/)
    .map(p => p.trim())
    .filter(Boolean);

  const bodyPartPatterns = [
    /Hips?\s*[-–—]?\s*Bilateral/gi,
    /Knees?\s*[-–—]?\s*Bilateral/gi,
    /Shoulders?\s*[-–—]?\s*Bilateral/gi,
    /((?:Left|Right)\s+)?(?:Knee|Hip|Shoulder|Elbow|Hand|Wrist|Foot|Ankle)(?:\s*[-–—]\s*(?:Left|Right))?/gi,
    /(?:Lumbar|Cervical)\s+Spine(?:\s*[-–—]\s*Priority)?/gi,
    /Lower\s+Back/gi,
    /Neck\s*(?:[-–—]\s*(?:Left|Right|Priority))?/gi,
    /(?:Left|Right)\s+(?:Lower\s+)?Leg/gi,
  ];

  const foundParts: string[] = [];
  for (const pattern of bodyPartPatterns) {
    const matches = text.match(pattern);
    if (matches) {
      foundParts.push(...matches);
    }
  }

  if (rawParts.length === 0 || normalizeBodyParts(rawParts).length === 0) {
    rawParts = foundParts;
  } else if (foundParts.length > 0) {
    const existingLower = new Set(rawParts.map(p => p.toLowerCase()));
    for (const found of foundParts) {
      if (!existingLower.has(found.toLowerCase())) {
        rawParts.push(found);
      }
    }
  }

  if (rawParts.length > 0) {
    const normalized = normalizeBodyParts(rawParts);
    fields.body_parts = [...new Set(normalized)];
    if (fields.body_parts.length > 0) {
      fields.invited_joint = fields.body_parts[0];
    }
  }

  const patientNameMatch = text.match(/Re:\s*((?:Mrs?|Ms|Mr|Dr|Miss)\s+[A-Za-z\s]+?)(?=,\s*\d)/i);
  if (patientNameMatch) fields.patient_name = patientNameMatch[1].trim();

  const addressMatch = text.match(/Re:\s*(?:Mrs?|Ms|Mr|Dr|Miss)\s+[A-Za-z\s]+?,\s*(.+?)(?=\s*(?:Telephone|DOB|Email))/i);
  if (addressMatch) fields.address = addressMatch[1].trim();

  const patientSectionMatch = text.match(/Re:\s*.+?(?=\s*GP:)/is);
  const patientSection = patientSectionMatch ? patientSectionMatch[0] : "";

  const phoneMatch = patientSection.match(/Telephone:\s*(.+?)(?=\s*(?:Email|DOB|NHS\s+No|$))/is);
  if (phoneMatch) {
    const phoneText = phoneMatch[1].replace(/\s+/g, " ");
    const phones = phoneText
      .split(/[,;]/)
      .map(p => p.replace(/[^\d]/g, ""))
      .filter(p => p.length >= 10 && p.length <= 12);
    const normalizedPhones = phones.map(p => p.startsWith("0") ? "+44" + p.slice(1) : p);
    if (normalizedPhones.length > 0) {
      fields.phone_numbers = normalizedPhones;
      const mobile = normalizedPhones.find(p => p.startsWith("+447"));
      fields.phone = mobile || normalizedPhones[0];
    }
  }

  const emailMatch = text.match(/Email(?:\s+Address)?:\s*([^\s]+@[^\s]+)/i);
  if (emailMatch) fields.email = emailMatch[1].replace(/\(Unverified\)/i, "").trim();

  const dobMatch = text.match(/DOB:\s*(\d{1,2}\s+[A-Za-z]+\s+\d{4})/i);
  if (dobMatch) {
    const parsed = parseDate(dobMatch[1]);
    if (parsed) fields.date_of_birth = parsed;
  }

  const nhsMatch = text.match(/NHS\s+No:?\s*([\d\s]+)/i);
  if (nhsMatch) fields.nhs_number = nhsMatch[1].replace(/\s+/g, "").trim();

  const gpMatch = text.match(/GP:\s*([^,]+)/i);
  if (gpMatch) fields.gp_name = gpMatch[1].trim();

  const gpAddressMatch = text.match(/GP:\s*[^,]+,\s*(.+?)(?=\s*Telephone)/i);
  if (gpAddressMatch) fields.gp_address = gpAddressMatch[1].trim();

  return fields;
}

function parseDate(dateStr: string): string | null {
  const months: { [key: string]: string } = {
    jan: "01", feb: "02", mar: "03", apr: "04",
    may: "05", jun: "06", jul: "07", aug: "08",
    sep: "09", oct: "10", nov: "11", dec: "12",
  };

  const match = dateStr.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
  if (!match) return null;

  const day = match[1].padStart(2, "0");
  const monthName = match[2].toLowerCase().slice(0, 3);
  const year = match[3];
  const month = months[monthName];

  return month ? `${year}-${month}-${day}` : null;
}

function normalizeBodyParts(bodyParts: string[]): string[] {
  const unilateralJoints = ["neck", "lower_back"];
  const jointMapping: [string, string][] = [
    ["cervical", "neck"],
    ["lumbar", "lower_back"],
    ["lower back", "lower_back"],
    ["spine", "lower_back"],
    ["lower leg", "lower_leg"],
    ["neck", "neck"],
    ["knee", "knee"],
    ["hip", "hip"],
    ["shoulder", "shoulder"],
    ["hand", "hand"],
    ["wrist", "wrist"],
    ["foot", "foot"],
    ["ankle", "foot"],
    ["elbow", "elbow"],
  ];

  const result: string[] = [];

  for (const part of bodyParts) {
    const normalized = part.toLowerCase();
    let baseJoint: string | null = null;
    for (const [key, value] of jointMapping) {
      if (normalized.includes(key)) {
        baseJoint = value;
        break;
      }
    }

    if (!baseJoint) continue;

    if (unilateralJoints.includes(baseJoint)) {
      result.push(baseJoint);
      continue;
    }

    const isBilateral = normalized.includes("bilateral") ||
                        normalized.includes("both") ||
                        (normalized.endsWith("s") && !normalized.match(/[-–—]/));

    if (isBilateral) {
      result.push(`left_${baseJoint}`);
      result.push(`right_${baseJoint}`);
      continue;
    }

    const isLeft = normalized.includes("left");
    const isRight = normalized.includes("right");

    if (isLeft) {
      result.push(`left_${baseJoint}`);
    } else if (isRight) {
      result.push(`right_${baseJoint}`);
    } else {
      result.push(baseJoint);
    }
  }

  return result;
}
